import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSWR from 'swr';
import { Check, CircleHelp, Copy, Download, ExternalLink, ImagePlus, Loader2, Lock, LockOpen, MapPin, QrCode, Settings2, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCan } from '@/hooks/useCan';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { refreshZoneFeatures } from '@/hooks/useZoneFeatures';
import { FeatureDisableLink, FeatureGate } from '@/components/features/FeatureGate';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import {
  apiUrl, deleteReviewFormLogo, getReviewsSettings, publicReviewUrl, putReviewForm, putReviewSource,
  uploadReviewFormLogo,
  type FormIdentity, type FormOptions, type FormTextKey, type QrLogo, type FormTexts, type MapProvider, type ReviewFormDto,
  type ReviewSourceDto, type ReviewsSettingsDto, type TextSize, type TextWeight,
} from '@/api/reviews';
import { DEFAULT_OPTIONS, ReviewFormView, textOf } from './ReviewFormView';
import { Popover } from '@/components/ui/popover';
import { TextStyleGear } from './TextStyleGear';
import { Tooltip } from '@/components/ui/tooltip';
import { PhoneFrame } from './PhoneFrame';
import { ImageLightbox } from '@/components/ui/image-lightbox';
import { QrHowto } from './QrHowto';
import { QrImageDialog } from './QrImageDialog';
import { QrCardDesigner, QrCardPreview, QrCardPrintDialog, useQrBackgrounds, withCardDefaults } from './QrCard';
import { Link } from 'react-router-dom';

const IDENTITY_LOGO: Record<FormIdentity, string> = {
  linza: '/brand/linza-robot.jpg',
  surf: '/brand/surf-coffee.png',
};

export default function ReviewsSettingsPage() {
  return (
    <FeatureGate feature="reviews" path="/management/reviews">
      <ReviewsSettings />
      <FeatureDisableLink feature="reviews" />
    </FeatureGate>
  );
}

function ReviewsSettings() {
  const { zoneId, currentZone, mutate: mutateZones } = useCurrentZone();
  const canEdit = useCan('zone.config.edit', zoneId);
  const { data, mutate, isLoading } = useSWR<ReviewsSettingsDto>(
    zoneId ? ['reviews-settings', zoneId] : null,
    async () => (await getReviewsSettings(zoneId!)).data,
    { revalidateOnFocus: false },
  );
  // A freshly saved link syncs in the background: poll until the first result lands.
  const syncing = !!data && (['yandex', '2gis'] as MapProvider[]).some((p) => {
    const s = data.sources[p];
    return s && !s.last_attempt_at;
  });
  useEffect(() => {
    if (!syncing) return;
    const id = window.setInterval(() => void mutate(), 5000);
    return () => window.clearInterval(id);
  }, [syncing, mutate]);

  if (isLoading || !data || !zoneId) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Загрузка…</div>;
  }
  const onChanged = async () => {
    await mutate();
    refreshZoneFeatures(zoneId);
    await mutateZones();
  };
  return (
    <div className="space-y-4">
      <MapsCard zoneId={zoneId} data={data} canEdit={canEdit} onChanged={onChanged} />
      <QrCard
        zoneId={zoneId}
        data={data}
        address={currentZone?.address ?? data.address}
        canEdit={canEdit}
        onChanged={onChanged}
      />
    </div>
  );
}

// ── Maps ─────────────────────────────────────────────────────────────────────

const PROVIDERS: { key: MapProvider; label: string; example: string }[] = [
  { key: 'yandex', label: 'Яндекс Карты', example: 'https://yandex.ru/maps/org/126977623704/' },
  { key: '2gis', label: '2ГИС', example: 'https://2gis.ru/moscow/firm/70000001234567890' },
];

function MapsCard({ zoneId, data, canEdit, onChanged }: {
  zoneId: string; data: ReviewsSettingsDto; canEdit: boolean; onChanged: () => Promise<void>;
}) {
  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MapPin className="size-4" /> Отзывы с карт</CardTitle>
        <CardDescription>
          Вставьте ссылку на карточку филиала. Каждую ночь мы забираем рейтинг, число отзывов и новые отзывы —
          они появятся в Данных → Отзывы и в разделе «Другое» отчёта дня.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {PROVIDERS.map((p) => (
          <SourceRow key={p.key} zoneId={zoneId} provider={p} source={data.sources[p.key]} canEdit={canEdit} onChanged={onChanged} />
        ))}
      </CardContent>
    </Card>
  );
}

function fmtWhen(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Формат, который парсер берёт без сетевых запросов (#5926) — как parse_source_link на бэке:
// ссылка на карточку с числовым ID. Короткие (yandex.ru/maps/-/…, go.2gis.com/…) и слаг-ссылки — нет.
const LINK_FORMAT_HINT = 'Откройте ссылку в браузере и вставьте полную ссылку на карточку организации, например https://yandex.ru/maps/org/<ID>/ или https://2gis.ru/moscow/firm/<ID>';
const LINK_RULES: Record<MapProvider, { host: RegExp; id: RegExp }> = {
  yandex: { host: /^(?:www\.)?(?:yandex\.(?:ru|com)|ya\.ru)$/i, id: /\/maps\/org\/(?:[^/?#]+\/)?(\d{6,})/ },
  '2gis': { host: /^(?:www\.)?2gis\.(?:ru|com)$/i, id: /\/(?:firm|branches)\/(\d{10,})/ },
};

function isValidSourceLink(provider: MapProvider, raw: string): boolean {
  const v = raw.trim();
  if (!v) return true; // пусто = убрать источник
  try {
    const u = new URL(v.includes('://') ? v : `https://${v}`);
    const rule = LINK_RULES[provider];
    return rule.host.test(u.hostname) && rule.id.test(u.pathname);
  } catch {
    return false;
  }
}

function SourceRow({ zoneId, provider, source, canEdit, onChanged }: {
  zoneId: string;
  provider: { key: MapProvider; label: string; example: string };
  source: ReviewSourceDto | null;
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const [url, setUrl] = useState(source?.url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setUrl(source?.url ?? ''); }, [source?.url]);
  const dirty = url.trim() !== (source?.url ?? '');
  const invalid = dirty && !isValidSourceLink(provider.key, url);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await putReviewSource(zoneId, provider.key, url.trim());
      await onChanged();
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить ссылку'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">{provider.label}</span>
        {source && (
          <span className="text-xs text-muted-foreground">
            {source.rating !== null && <>★ {source.rating.toFixed(1)} · </>}
            {source.review_count !== null && <>{source.review_count} отзывов · </>}
            {source.last_success_at
              ? <>обновлено {fmtWhen(source.last_success_at)}</>
              : source.last_attempt_at ? 'не удалось получить' : <span className="inline-flex items-center gap-1"><Loader2 className="size-3 animate-spin" /> первая загрузка…</span>}
            {source.public_url && (
              <a href={source.public_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-primary hover:underline">
                открыть <ExternalLink className="size-3" />
              </a>
            )}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={provider.example}
          disabled={!canEdit || busy}
          aria-invalid={invalid}
          className={cn('text-sm', invalid && 'border-destructive focus-visible:ring-destructive')}
        />
        {canEdit && (dirty || busy) && (
          <Button onClick={() => void save()} disabled={busy || invalid} size="default">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {url.trim() ? 'Сохранить' : 'Убрать'}
          </Button>
        )}
      </div>
      {source?.last_error && (
        <p className="text-xs text-destructive">Последняя загрузка не удалась: {source.last_error}. Мы уже знаем и чиним.</p>
      )}
      {invalid && <p className="text-xs text-destructive">{LINK_FORMAT_HINT}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── QR form ─────────────────────────────────────────────────────────────────

function QrCard({ zoneId, data, address, canEdit, onChanged }: {
  zoneId: string; data: ReviewsSettingsDto; address: string; canEdit: boolean; onChanged: () => Promise<void>;
}) {
  const form = data.form;
  const [creating, setCreating] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [stickersOpen, setStickersOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setCreating(true);
    setError(null);
    try {
      await putReviewForm(zoneId, {
        identity: 'linza',
        show_address: true,
        texts: { title: { text: data.branch_name, size: 'xl' } },
      });
      // Остаёмся на странице: дальше «Настроить форму» (Алексей 22.10, #1976 — перекидывало в редактор).
      await onChanged();
    } catch (e) {
      setError(extractApiError(e, 'Не удалось создать форму'));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Card variant="section">
      {/* Телефон — правая колонка ВСЕЙ карточки, а не одной её секции (Алексей
          18.09, AI59). Раньше он жил внутри нижнего блока и центрировался по
          нему, поэтому сверху зиял лишний зазор. Отступы справа/сверху/снизу
          одинаковые и чуть больше карточных.
          Левая колонка прижата к верху (Алексей 21.09, AI98): телефон справа
          выше, и при центрировании заголовок с описанием висели посреди
          карточки, оторванные от её шапки. */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
        <div className="min-w-0">
      <CardHeader>
        {/* «Как пользоваться» — в самой шапке рядом с заголовком, текст — одной
            фразой (Алексей 17.09, AI13/AI16.6). */}
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2"><QrCode className="size-4" /> Отзывы по QR-коду</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setHowtoOpen(true)}>
            <CircleHelp className="size-3.5" /> Как пользоваться
          </Button>
        </div>
        <CardDescription>
          Распечатайте QR-код, поставьте его на кассе или на столах, смотрите сводки в{' '}
          <Link to="/management/analytics?tab=reviews" className="text-primary hover:underline">«Данных»</Link>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!form ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-8 text-center">
            <QrCode className="size-10 text-muted-foreground" />
            {canEdit && (
              <Button onClick={() => void create()} disabled={creating}>
                {creating && <Loader2 className="size-4 animate-spin" />} Создать форму отзыва
              </Button>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        ) : (
          <QrPanel
            zoneId={zoneId} form={form} branchName={data.branch_name} canEdit={canEdit} onChanged={onChanged}
            onEdit={() => setEditorOpen(true)} onConfigureCard={() => setCardOpen(true)}
            onPrint={() => setPrintOpen(true)} onStickers={() => setStickersOpen(true)}
          />
        )}
      </CardContent>
        </div>
        {form && (
          <div className="hidden lg:block lg:py-6 lg:pr-6">
            <PhoneFrame scale={0.62}>
              <ReviewFormView identity={form.identity} branchName={data.branch_name} address={form.show_address ? address : ''} texts={form.texts} options={form.options} />
            </PhoneFrame>
          </div>
        )}
      </div>
      <QrHowto open={howtoOpen} onOpenChange={setHowtoOpen} hasForm={!!form} onPrint={form ? () => setPrintOpen(true) : undefined} />
      {form && (
        <>
          <QrCardDesigner
            open={cardOpen}
            onOpenChange={setCardOpen}
            zoneId={zoneId}
            url={publicReviewUrl(form.public_id)}
            logoSrc={qrLogoSrc(form)}
            initial={withCardDefaults(form.options.qr_card, data.branch_name)}
            surf={isSurf(form)}
            onSaved={onChanged}
          />
          <QrCardPrintDialog
            open={printOpen}
            onOpenChange={setPrintOpen}
            zoneId={zoneId}
            url={publicReviewUrl(form.public_id)}
            logoSrc={qrLogoSrc(form)}
            card={withCardDefaults(form.options.qr_card, data.branch_name)}
            surf={isSurf(form)}
            onConfigure={canEdit ? () => setCardOpen(true) : undefined}
          />
        </>
      )}
      {form && (
        <QrImageDialog
          open={stickersOpen}
          onOpenChange={setStickersOpen}
          url={publicReviewUrl(form.public_id)}
          logoSrc={qrLogoSrc(form)}
          identity={form.identity}
          defaultCaption={data.branch_name}
        />
      )}
      {form && editorOpen && (
        <FormEditor zoneId={zoneId} form={form} data={data} address={address} onClose={() => setEditorOpen(false)} onSaved={onChanged} />
      )}
    </Card>
  );
}

/** Surf-филиалу — Surf-шаблоны карточки (#2009): оформление формы Surf или стиль сети Surf. */
const isSurf = (form: ReviewFormDto) => form.identity === 'surf' || form.brand_style?.identity === 'surf';

/** Логотип в центре QR — свой выбор (робот / серф / без / загруженный), не
 *  зависит от оформления формы (AI16.4). null — код без логотипа. */
function qrLogoSrc(form: ReviewFormDto): string | null {
  const choice = form.options.qr_logo ?? (form.has_logo ? 'custom' : form.identity === 'surf' ? 'surf' : 'robot');
  if (choice === 'none') return null;
  if (choice === 'custom' && form.logo_url) return apiUrl(form.logo_url);
  if (choice === 'surf') return IDENTITY_LOGO.surf;
  return IDENTITY_LOGO.linza;
}

function QrPanel({ zoneId, form, branchName, canEdit, onChanged, onEdit, onConfigureCard, onPrint, onStickers }: {
  zoneId: string; form: ReviewFormDto; branchName: string; canEdit: boolean;
  onChanged: () => Promise<void>; onEdit: () => void; onConfigureCard: () => void; onPrint: () => void; onStickers: () => void;
}) {
  const url = publicReviewUrl(form.public_id);
  const logoSrc = qrLogoSrc(form);
  const logoChoice = form.options.qr_logo ?? (form.has_logo ? 'custom' : form.identity === 'surf' ? 'surf' : 'robot');
  const card = withCardDefaults(form.options.qr_card, branchName);
  const { data: bgs } = useQrBackgrounds(zoneId);
  const cardCanvas = useRef<HTMLCanvasElement | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Клик по коду открывает его во весь экран — чтобы показать гостю прямо с
  // телефона, не печатая (Алексей 17.09, AI35).
  const [zoom, setZoom] = useState<string | null>(null);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(extractApiError(e, 'Не получилось'));
    } finally {
      setBusy(null);
    }
  }

  const section = (title: string, children: React.ReactNode, className?: string) => (
    <section className={cn('space-y-3', className)}>
      <h4 className="text-sm font-semibold">{title}</h4>
      {children}
    </section>
  );

  const LOGO_OPTIONS: { key: QrLogo; label: string; img?: string }[] = [
    { key: 'robot', label: 'Робот', img: IDENTITY_LOGO.linza },
    { key: 'surf', label: 'Surf', img: IDENTITY_LOGO.surf },
    { key: 'none', label: 'Без логотипа' },
  ];

  return (
    // Слева сверху — «Ссылка на форму» (телефон справа показывает её), слева
    // снизу — «QR-код»: код и справа от него настройки (Алексей 17.09, AI16.1).
    <div className="space-y-6">
      {section('Ссылка на форму', (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input readOnly value={url} className={cn('font-mono text-xs', !form.is_active && 'text-muted-foreground line-through')} onFocus={(e) => e.currentTarget.select()} />
            <Button
              variant="outline"
              onClick={() => { void navigator.clipboard.writeText(url); setCopied(true); window.setTimeout(() => setCopied(false), 1500); }}
            >
              {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {copied ? 'Скопировано' : 'Копировать'}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && <Button size="sm" onClick={onEdit}><Settings2 className="size-3.5" /> Настроить форму</Button>}
            <a href={url} target="_blank" rel="noreferrer" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>
              <ExternalLink className="size-3.5" /> Открыть страницу
            </a>
            {/* Вместо тумблера «Страница опубликована» — кнопка (AI16.3): закрытая
                ссылка отдаёт «Страница не найдена». */}
            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                onClick={() => void run('active', () => putReviewForm(zoneId, { is_active: !form.is_active }))}
              >
                {busy === 'active' ? <Loader2 className="size-3.5 animate-spin" /> : form.is_active ? <Lock className="size-3.5" /> : <LockOpen className="size-3.5" />}
                {form.is_active ? 'Закрыть ссылку' : 'Открыть ссылку'}
              </Button>
            )}
          </div>
          {!form.is_active && (
            <p className="text-xs text-muted-foreground">Ссылка закрыта: по ней и по QR-коду гость увидит «Страница не найдена».</p>
          )}
        </div>
      ))}

      {section('Карточка с QR-кодом', (
        <div className="flex flex-col gap-4 sm:flex-row">
          <Tooltip content="Показать карточку на весь экран"><button
            type="button"
            onClick={() => cardCanvas.current?.toBlob((b) => { if (b) setZoom(URL.createObjectURL(b)); })}
            className="shrink-0 self-start rounded-md transition-shadow hover:shadow-md"
            aria-label="Показать карточку на весь экран"
          >
            <QrCardPreview url={url} logoSrc={logoSrc} card={card} list={bgs?.backgrounds} surf={isSurf(form)} width={900} className="w-36" onRendered={(c) => { cardCanvas.current = c; }} />
          </button></Tooltip>
          {zoom && (
            <ImageLightbox
              srcs={[zoom]}
              index={0}
              alt="Карточка с QR-кодом для отзывов"
              onNavigate={() => {}}
              onClose={() => { URL.revokeObjectURL(zoom); setZoom(null); }}
            />
          )}
          <div className="min-w-0 flex-1 space-y-4 text-sm">
            {canEdit && (
              <div className="space-y-1.5">
                <div className="text-muted-foreground">Логотип в центре кода:</div>
                <div className="flex flex-wrap items-center gap-2">
                  {LOGO_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      disabled={busy !== null}
                      onClick={() => void run('logo', () => putReviewForm(zoneId, { options: { qr_logo: o.key } }))}
                      className={cn(
                        'flex h-8 items-center gap-2 rounded-lg border px-2.5 text-sm transition-colors',
                        logoChoice === o.key ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                      )}
                    >
                      {o.img && <img src={o.img} alt="" className="size-5 rounded object-contain" />}
                      {o.label}
                    </button>
                  ))}
                  {form.has_logo && (
                    <span
                      className={cn(
                        'flex h-8 items-center gap-1 rounded-lg border pl-1 text-sm transition-colors',
                        logoChoice === 'custom' ? 'border-primary bg-primary/5' : 'border-border',
                      )}
                    >
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void run('logo', () => putReviewForm(zoneId, { options: { qr_logo: 'custom' } }))}
                        className="flex items-center gap-2 px-1.5"
                      >
                        {form.logo_url && <img src={apiUrl(form.logo_url)} alt="" className="size-5 rounded object-contain" />}
                        Свой
                      </button>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void run('logo', () => deleteReviewFormLogo(zoneId))}
                        className="grid h-full place-items-center rounded-r-lg px-1.5 text-muted-foreground hover:bg-muted"
                        aria-label="Убрать свой логотип"
                      >
                        <X className="size-3.5" />
                      </button>
                    </span>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f) void run('logo', () => uploadReviewFormLogo(zoneId, f));
                    }}
                  />
                  <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
                    <ImagePlus className="size-3.5" /> Загрузить
                  </Button>
                  {busy === 'logo' && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
                </div>
              </div>
            )}
            {/* Одна кнопка вместо «Печать» + сплит-кнопки скачивания: всё, что
                отличалось, переехало внутрь модалки (Алексей 18.09, AI57). */}
            {/* «Настроить» и «Скачать» раздельно, как у формы (Алексей 22.10, #1976). */}
            <div className="flex flex-wrap items-center gap-2">
              {canEdit && <Button size="sm" onClick={onConfigureCard}><Settings2 className="size-3.5" /> Настроить карточку</Button>}
              <Button size="sm" variant="outline" onClick={onPrint}><Download className="size-3.5" /> Скачать</Button>
              <Button size="sm" variant="ghost" onClick={onStickers}><QrCode className="size-3.5" /> Наклейки для столов</Button>
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
        </div>
      ), 'border-t border-border pt-6')}

    </div>
  );
}

// ── Text editor with live phone preview ─────────────────────────────────────

const TEXT_FIELDS: { key: FormTextKey; label: string; hint: string }[] = [
  { key: 'title', label: 'Заголовок', hint: 'Обычно название филиала, например «Surf Coffee x Ostozhenka»' },
  { key: 'rating_label', label: 'Над звёздами', hint: 'Например «Оцените визит». Оценка всегда необязательна' },
  { key: 'prompt', label: 'Над полем отзыва', hint: 'Например «Что скажете?»' },
  { key: 'prompt_subtitle', label: 'Подзаголовок', hint: 'Пояснение под вопросом, можно оставить пустым' },
  { key: 'hint', label: 'Под полем отзыва', hint: 'Что написать, например «Что понравилось, что улучшить»' },
];
const SIZES: { key: TextSize; label: string }[] = [
  { key: 'sm', label: 'S' }, { key: 'md', label: 'M' }, { key: 'lg', label: 'L' }, { key: 'xl', label: 'XL' },
];


function FormEditor({ zoneId, form, data, address, onClose, onSaved }: {
  zoneId: string; form: ReviewFormDto; data: ReviewsSettingsDto; address: string; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const initial = useMemo(() => {
    const out: FormTexts = {};
    for (const f of [...TEXT_FIELDS.map((x) => x.key), 'address' as FormTextKey]) out[f] = textOf(form.texts, f);
    if (!out.title?.text) out.title = { text: data.branch_name, size: out.title?.size ?? 'xl' };
    return out;
  }, [form.texts, data.branch_name]);
  const [texts, setTexts] = useState<FormTexts>(initial);
  const [identity, setIdentity] = useState<FormIdentity>(form.identity);
  const [options, setOptions] = useState<FormOptions>(form.options ?? DEFAULT_OPTIONS);
  const [showAddress, setShowAddress] = useState(form.show_address);
  const [addressDraft, setAddressDraft] = useState(address);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addressMissing = !address.trim();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const set = (key: FormTextKey, patch: Partial<{ text: string; size: TextSize; weight: TextWeight; caps: boolean }>) =>
    setTexts((prev) => ({ ...prev, [key]: { ...textOf(prev, key), ...patch } }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await putReviewForm(zoneId, {
        texts,
        identity,
        options,
        show_address: showAddress,
        ...(showAddress && addressMissing && addressDraft.trim() ? { address: addressDraft.trim() } : {}),
      });
      await onSaved();
      onClose();
    } catch (e) {
      setError(extractApiError(e, 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  }

  const previewAddress = showAddress ? (addressMissing ? addressDraft : address) : '';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6" data-overlay>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div role="dialog" aria-modal="true" className="relative z-10 flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-semibold">Форма отзыва</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted" aria-label="Закрыть">
            <X className="size-4" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5 md:flex-row">
          <div className="min-w-0 flex-1 space-y-4">
            {/* Оформление можно сменить прямо здесь, не выходя из редактора (п. 12). */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Оформление:</span>
              {(['linza', 'surf'] as FormIdentity[]).map((idt) => (
                <button
                  key={idt}
                  type="button"
                  onClick={() => setIdentity(idt)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-2.5 py-1 text-sm transition-colors',
                    identity === idt ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                  )}
                >
                  <img src={IDENTITY_LOGO[idt]} alt="" className="size-5 rounded object-contain" />
                  {idt === 'linza' ? 'Linza' : 'Surf Coffee'}
                  {/* оформление сети филиала (плагин сети, #1279) — помечаем, но выбор оставляем */}
                  {form.brand_style?.identity === idt && (
                    <span className="text-xs text-muted-foreground">· стиль сети</span>
                  )}
                </button>
              ))}
            </div>
            {TEXT_FIELDS.slice(0, 1).map((f) => (
              <TextField key={f.key} field={f} value={textOf(texts, f.key)} onChange={(p) => set(f.key, p)} />
            ))}
            <div className="space-y-2 rounded-lg border border-border p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input type="checkbox" checked={showAddress} onChange={(e) => setShowAddress(e.target.checked)} />
                Показывать адрес под заголовком
              </label>
              {showAddress && (
                <div className="flex items-center gap-2">
                  {addressMissing ? (
                    <Input
                      value={addressDraft}
                      onChange={(e) => setAddressDraft(e.target.value)}
                      placeholder="Адрес филиала — сохранится и в общих настройках"
                      className="text-sm"
                    />
                  ) : (
                    <span className="flex-1 text-sm text-muted-foreground">{address} <span className="text-xs">(из общих настроек)</span></span>
                  )}
                  <TextStyleGear value={textOf(texts, 'address')} onChange={(p) => set('address', p)} />
                </div>
              )}
            </div>
            {/* Звёзды — опциональны, по умолчанию включены (п. 9). */}
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={options.stars} onChange={(e) => setOptions((o) => ({ ...o, stars: e.target.checked }))} />
              Оценка звёздами
            </label>
            {options.stars && TEXT_FIELDS.filter((f) => f.key === 'rating_label').map((f) => (
              <TextField key={f.key} field={f} value={textOf(texts, f.key)} onChange={(p) => set(f.key, p)} />
            ))}
            {TEXT_FIELDS.filter((f) => f.key !== 'title' && f.key !== 'rating_label').map((f) => (
              <TextField key={f.key} field={f} value={textOf(texts, f.key)} onChange={(p) => set(f.key, p)} />
            ))}
            {/* Поле контакта: у гостя — одно необязательное поле в стиле поля
                отзыва, подпись — плейсхолдер (AI16.7). */}
            <div className="space-y-2 rounded-lg border border-border p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={options.contact.enabled}
                  onChange={(e) => setOptions((o) => ({ ...o, contact: { ...o.contact, enabled: e.target.checked } }))}
                />
                Поле для контакта гостя
              </label>
              {options.contact.enabled && (
                <Input
                  value={options.contact.text}
                  onChange={(e) => setOptions((o) => ({ ...o, contact: { ...o.contact, text: e.target.value.slice(0, 300) } }))}
                  placeholder="Подпись в поле, например «Ваш контакт (необязательно)»"
                  className="text-sm"
                />
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <div className="flex shrink-0 flex-col items-center justify-center">
            <PhoneFrame scale={0.78}>
              <ReviewFormView identity={identity} branchName={data.branch_name} address={previewAddress} texts={texts} options={options} />
            </PhoneFrame>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving && <Loader2 className="size-4 animate-spin" />} Сохранить
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TextField({ field, value, onChange }: {
  field: { key: FormTextKey; label: string; hint: string };
  value: { text: string; size: TextSize; weight: TextWeight };
  onChange: (p: Partial<{ text: string; size: TextSize; weight: TextWeight }>) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">{field.label}</div>
      <div className="flex items-center gap-2">
        <Input value={value.text} onChange={(e) => onChange({ text: e.target.value.slice(0, 300) })} placeholder={field.hint} className="text-sm" />
        <TextStyleGear value={value} onChange={onChange} />
      </div>
    </div>
  );
}
